import React, { useRef, useMemo } from 'react';
import { mergeProps } from '@react-aria/utils';
import { useHover } from '@react-aria/interactions';
import { useCheckbox } from '@react-aria/checkbox';
import { useToggleState } from '@react-stately/toggle';
import { useFocusRing } from '@react-aria/focus';
import { useCheckboxGroupContext } from '../checkbox-group/checkbox-group-context.component.js';
import { VisuallyHidden } from '../visually-hidden/index.js';
import { CheckboxLabel } from './checkbox.styles.js';
import { CheckboxDefault } from './checkbox-default.component.js';
import { CheckboxChip } from './checkbox-chip.component.js';
export const Checkbox = React.forwardRef(({ label, excludeFromTabOrder, lang, className, dataAutomation, style, disableHoverStyling, disableInteractions, startIcon, showSelectionAsPartial, ...props }, ref) => {
    const checkboxRef = useRef(null);
    const { groupState, checkboxProps } = useCheckboxGroupContext();
    const isLoading = props.loading || checkboxProps?.loading;
    const isReadOnly = props.isReadOnly || checkboxProps?.isReadOnly;
    const toggleState = useToggleState({
        ...mergeProps({
            isSelected: props.value
                ? groupState?.isSelected(props.value)
                : undefined,
            isReadOnly: isLoading || isReadOnly,
        }, props),
        onChange(isSelected) {
            groupState?.toggleValue(props.value);
            props.onChange?.(isSelected);
        },
    });
    const { inputProps } = useCheckbox({
        children: label,
        excludeFromTabOrder,
        ...mergeProps({
            isDisabled: groupState?.isDisabled,
            isReadOnly: isLoading || isReadOnly ? props.isReadOnly : undefined,
            onBlur: checkboxProps?.onBlur,
            isRequired: checkboxProps?.isRequired,
        }, props),
    }, toggleState, checkboxRef);
    const { isFocusVisible, focusProps } = useFocusRing();
    const isInteractionDisabled = props.isDisabled || isLoading || isReadOnly;
    const { hoverProps, isHovered } = useHover({
        isDisabled: isInteractionDisabled,
    });
    const uniqueDataAutomation = useMemo(() => dataAutomation ||
        ['checkbox', props.id || props.name || ''].filter(Boolean).join('-'), [dataAutomation, props.id, props.name]);
    const groupItemDataAutomation = `${checkboxProps?.dataAutomation}/${uniqueDataAutomation}`;
    return (React.createElement(CheckboxLabel, { lang: lang, className: className, "data-automation": checkboxProps ? groupItemDataAutomation : uniqueDataAutomation, style: style, ref: ref, ...hoverProps, isSelected: toggleState.isSelected, disableInteractions: disableInteractions, isDisabled: inputProps.disabled, loading: isLoading, isReadOnly: isReadOnly },
        React.createElement(VisuallyHidden, { elementType: 'span' },
            React.createElement("input", { ...mergeProps(inputProps, focusProps), ref: checkboxRef, "data-automation": `${checkboxProps ? groupItemDataAutomation : uniqueDataAutomation}/input/${toggleState.isSelected ? 'active' : 'inactive'}`, name: checkboxProps ? checkboxProps?.name : props.name })),
        checkboxProps?.variant === 'chips' ? (React.createElement(CheckboxChip, { isSelected: toggleState.isSelected, isFocusVisible: !excludeFromTabOrder && isFocusVisible, isLoading: !!isLoading, isDisabled: !!inputProps.disabled, label: label, startIcon: startIcon })) : (React.createElement(CheckboxDefault, { isHovered: !disableHoverStyling && !inputProps.disabled && isHovered, isSelected: toggleState.isSelected, showSelectionAsPartial: showSelectionAsPartial, isFocusVisible: !excludeFromTabOrder && isFocusVisible, isLoading: !!isLoading, label: label }))));
});
//# sourceMappingURL=checkbox.component.js.map